import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react-native';
import React, { memo } from 'react';
import {
  FlatList,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import DropdownSelect from 'react-native-input-select';

import { DateFormatter } from '@/helpers/DateFormatter';
import type { ISyncList } from '@/libs/types/sync';
import { Color } from '@/styles/GlobalStyles';

import { mapInterviewStatus } from '../../helpers/interviewStatus';
import { CardRow } from '../CardRow';
import { MobileSortControl } from '../MobileSortControl';
import { Row } from '../Row';
import { styles } from './styles';

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

type ITableProps = {
  interviews: ISyncList[];
  onSort?: (key: string) => void;
  sortConfig?: SortConfig;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
};

function SortIcon({
  columnKey,
  sortConfig,
}: {
  columnKey: string;
  sortConfig?: SortConfig;
}) {
  if (!sortConfig || sortConfig.key !== columnKey) {
    return (
      <ArrowUpDown
        size={14}
        color={Color.gray[200]}
        style={{ marginLeft: 4 }}
      />
    );
  }

  if (sortConfig.direction === 'asc') {
    return (
      <ArrowUp
        size={14}
        color={Color.whiteSmoke_100}
        style={{ marginLeft: 4 }}
      />
    );
  }

  return (
    <ArrowDown
      size={14}
      color={Color.whiteSmoke_100}
      style={{ marginLeft: 4 }}
    />
  );
}

function TableComponent({
  interviews,
  onSort,
  sortConfig,
  currentPage,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
}: ITableProps) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <View style={{ width: '100%' }}>
      {isMobile && (
        <MobileSortControl onSort={onSort} sortConfig={sortConfig} />
      )}

      <ScrollView
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        style={{
          paddingRight: isMobile ? 0 : 0,
          width: '100%',
        }}
      >
        {!isMobile && (
          <View style={styles.rows}>
            <TouchableOpacity
              style={[
                styles.headerCells,
                styles.headerCellId,
                { flexDirection: 'row', alignItems: 'center' },
              ]}
              onPress={() => onSort?.('interview_id')}
            >
              <Text style={styles.text}>ID</Text>
              <SortIcon columnKey="interview_id" sortConfig={sortConfig} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerCells,
                { flexDirection: 'row', alignItems: 'center' },
              ]}
              onPress={() => onSort?.('form_name')}
            >
              <Text style={styles.text}>Questionário</Text>
              <SortIcon columnKey="form_name" sortConfig={sortConfig} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerCells,
                { flexDirection: 'row', alignItems: 'center' },
              ]}
              onPress={() => onSort?.('created_at')}
            >
              <Text style={styles.text}>Data de Envio</Text>
              <SortIcon columnKey="created_at" sortConfig={sortConfig} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerCells,
                { flexDirection: 'row', alignItems: 'center' },
              ]}
              onPress={() => onSort?.('duration')}
            >
              <Text style={styles.text}>Duração</Text>
              <SortIcon columnKey="duration" sortConfig={sortConfig} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.headerCells,
                styles.headerCellStatus,
                { flexDirection: 'row', alignItems: 'center' },
              ]}
              onPress={() => onSort?.('mapped_status')}
            >
              <Text style={styles.text}>Status</Text>
              <SortIcon columnKey="mapped_status" sortConfig={sortConfig} />
            </TouchableOpacity>
          </View>
        )}

        <FlatList
          data={interviews}
          keyExtractor={(item) => String(item.interview_id)}
          style={{ width: '100%' }}
          renderItem={({ item }) => {
            const {
              interview_id: interviewId,
              form_name: formName,
              created_at: createdAt,
              updated_at: updatedAt,
            } = item;
            const startDate =
              DateFormatter.fullDateWithHoursAndMinutes(createdAt);
            const duration = DateFormatter.duration({
              createdAt,
              updatedAt,
              options: ['hours', 'minutes', 'seconds'],
            });
            const status = mapInterviewStatus(item);

            if (isMobile) {
              return (
                <CardRow
                  id={interviewId}
                  form={formName}
                  startDate={startDate}
                  duration={duration}
                  status={status}
                />
              );
            }

            return (
              <Row
                id={interviewId}
                form={formName}
                startDate={startDate}
                duration={duration}
                status={status}
              />
            );
          }}
          showsVerticalScrollIndicator={false}
        />
      </ScrollView>

      {/* Pagination Footer */}
      <View style={styles.paginationContainer}>
        <View style={styles.itemsPerPageContainer}>
          <Text style={styles.paginationText}>Linhas por página:</Text>
          <View style={{ width: 80, height: 36 }}>
            <DropdownSelect
              options={[
                { label: '5', value: 5 },
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
              ]}
              selectedValue={itemsPerPage}
              onValueChange={(value: any) =>
                onItemsPerPageChange(Number(value))
              }
              placeholder=""
              primaryColor={Color.primary[300]}
              dropdownStyle={{
                backgroundColor: Color.gray[50],
                borderColor: Color.gray[100],
                borderWidth: 1,
                borderRadius: 8,
                height: 36,
                paddingVertical: 0,
                justifyContent: 'center',
                marginTop: 0,
                marginBottom: 0,
                minHeight: 36,
              }}
              dropdownIconStyle={{ top: 8, right: 8 }}
            />
          </View>
        </View>

        <Text style={styles.paginationText}>
          {startItem}-{endItem} de {totalItems}
        </Text>

        <View style={styles.paginationControls}>
          <TouchableOpacity
            style={[
              styles.pageButton,
              currentPage === 1 && styles.disabledButton,
            ]}
            onPress={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={20} color={Color.gray[300]} />
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.pageButton,
              currentPage >= totalPages && styles.disabledButton,
            ]}
            onPress={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight size={20} color={Color.gray[300]} />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

export const Table = memo(TableComponent);
